import { resolveLandingFaqs, type LandingContent } from '@/lib/landingContent';

export type Locale = 'tr' | 'en';

type L = { tr: string; en: string };

export type SolutionFaqItem = {
  question: L;
  answer: L;
};

export type SolutionDefinition = {
  slug: string;
  title: L;
  seoTitle: L;
  seoDescription: L;
  heroTitle: L;
  intro: L;
  legitimacyNote: L;
  whoFor: L[];
  whatWeBuild: L[];
  useCases: L[];
  outcomes: L[];
  delivery: L[];
  relatedServiceSlugs: string[];
  relatedProjectSlugs: string[];
  relatedBlogSlugs: string[];
  relatedToolIds: string[];
  faqs: SolutionFaqItem[];
  landing?: LandingContent;
};

export function localizeSolutionText(value: L, lang: Locale) {
  return value[lang];
}

const solutionDefinitions: SolutionDefinition[] = [
  {
    slug: 'chatgpt-integration-development',
    title: { tr: 'ChatGPT Entegrasyonu', en: 'ChatGPT Integration Development' },
    seoTitle: {
      tr: 'ChatGPT Entegrasyonu | Ürüne, CRM ve Destek Akışına',
      en: 'ChatGPT Integration Development | Product, CRM & Support',
    },
    seoDescription: {
      tr: 'ChatGPT entegrasyonu: OpenAI API’yi web sitenize, uygulamanıza, CRM ve destek sisteminize bağlıyoruz. RAG, KVKK, süreç, örnek fiyat bandı ve SSS.',
      en: 'ChatGPT integration development: the OpenAI API connected to your product, CRM and support tools, with RAG, guardrails, example bands and FAQ.',
    },
    heroTitle: {
      tr: 'ChatGPT entegrasyonu: OpenAI API’yi ürününüze ve iş akışlarınıza bağlıyoruz.',
      en: 'ChatGPT integration development for products, CRMs and support workflows.',
    },
    intro: {
      tr: 'Sadece chatbot eklemek yerine, ürününüzde gerçek kullanıcı değerine bağlanan ChatGPT akışları geliştiriyoruz.',
      en: 'Instead of just adding a chatbot, we build ChatGPT workflows connected to real user value in your product.',
    },
    legitimacyNote: {
      tr: 'Not: Figtures bağımsız bir ürün mühendisliği ekibidir; OpenAI veya ChatGPT ile resmi ortaklık/temsil iddiasında bulunmaz.',
      en: 'Note: Figtures is an independent product engineering team and does not claim official affiliation with OpenAI or ChatGPT.',
    },
    whoFor: [
      { tr: 'Destek ve operasyon süreçlerini AI ile hızlandırmak isteyen SaaS ekipleri', en: 'SaaS teams accelerating support and operations with AI' },
      { tr: 'Mevcut ürününe AI asistan eklemek isteyen platform ekipleri', en: 'Platform teams adding AI assistants into existing products' },
      { tr: 'Kullanıcıya self-serve bilgi deneyimi sunmak isteyen startup’lar', en: 'Startups creating self-serve knowledge experiences' },
    ],
    whatWeBuild: [
      { tr: 'ChatGPT tabanlı kullanıcı asistanları', en: 'ChatGPT-based user assistants' },
      { tr: 'Bilgi tabanı destekli cevaplama (RAG) akışları', en: 'Knowledge-grounded answering flows (RAG)' },
      { tr: 'Ticket triage ve destek otomasyon katmanları', en: 'Ticket triage and support automation layers' },
      { tr: 'Ürün içi özetleme, içerik üretimi ve analiz araçları', en: 'In-product summarization, generation, and analysis tools' },
    ],
    useCases: [
      { tr: 'Müşteri destek copilot', en: 'Customer support copilot' },
      { tr: 'Ürün içi akıllı arama', en: 'In-product intelligent search' },
      { tr: 'Operasyon ekibi için bilgi asistanı', en: 'Knowledge assistant for operations teams' },
    ],
    outcomes: [
      { tr: 'Daha hızlı kullanıcı yanıtı ve destek akışı', en: 'Faster user response and support workflows' },
      { tr: 'Tekrarlayan işlerde daha düşük manuel yük', en: 'Lower manual workload on repetitive tasks' },
      { tr: 'Ürün içinde daha yüksek self-serve kapasite', en: 'Higher self-serve capacity in product experiences' },
    ],
    delivery: [
      { tr: 'Discovery -> Prompt/flow design -> Integration -> Hardening', en: 'Discovery -> Prompt/flow design -> Integration -> Hardening' },
      { tr: 'Kalite değerlendirme, gözlemlenebilirlik ve fallback mekanizmaları', en: 'Quality evaluation, observability, and fallback mechanisms' },
    ],
    relatedServiceSlugs: ['ai-development', 'ai-automation-agents', 'custom-software-development'],
    relatedProjectSlugs: ['ztech', 'lcw'],
    relatedBlogSlugs: ['chatgpt-integration-business-guide', 'ai-development-company-selection-guide', 'evaluation-debt-in-ai-product-features'],
    relatedToolIds: ['customer-reply-generator', 'quote-proposal-writer', 'job-posting-writer'],
    faqs: [
      {
        question: { tr: 'ChatGPT entegrasyonu sadece sohbet ekranı mı demek?', en: 'Does ChatGPT integration only mean a chat interface?' },
        answer: { tr: 'Hayır. Ürün içi arama, özetleme, destek otomasyonu, içerik akışları ve operasyon yardımcıları dahil geniş kullanım alanı var.', en: 'No. It can include in-product search, summarization, support automation, content workflows, and operations assistants.' },
      },
      {
        question: { tr: 'Mevcut ürüne kademeli entegrasyon yapabilir misiniz?', en: 'Can you integrate incrementally into an existing product?' },
        answer: { tr: 'Evet. En yüksek etki üreten akışla başlayıp kontrollü sprintlerle genişletiyoruz.', en: 'Yes. We start with the highest-impact flow and expand in controlled sprints.' },
      },
      {
        question: { tr: 'ChatGPT entegrasyonu ne kadar tutar?', en: 'How much does a ChatGPT integration cost?' },
        answer: {
          tr: 'Maliyet tahmincimizde ChatGPT entegrasyonu için ayrı bir profil yok; en yakın profil olan genel / özel yazılım MVP bandı {price:general.mvp}, {duration:general.mvp} (örnek bant, KDV hariç). Küçük kapsamlı entegrasyonlar bu bandın altında kalabilir; kesin fiyat proje analizinden sonra yazılı teklifte belirlenir. OpenAI API kullanım ücreti ayrıca kullanıma göre oluşur.',
          en: 'Our cost estimator has no separate profile for ChatGPT integrations; the closest one, the general / custom software MVP band, is {price:general.mvp} over {duration:general.mvp} (example band, excl. VAT). Small integrations can fall below that band; the final price is set in a written proposal after the project analysis. OpenAI API usage is billed separately, based on usage.',
        },
      },
      {
        question: { tr: 'Müşteri verilerimiz OpenAI’ye gider mi? KVKK nasıl sağlanır?', en: 'Does our customer data go to OpenAI? How is GDPR handled?' },
        answer: {
          tr: 'Modele hangi verinin gönderileceği proje analizinde belirlenir. Kişisel veriler mümkün olduğunca azaltılır veya maskelenir; OpenAI API’nin veri kullanım koşulları ve erişim kayıtları teklif aşamasında yazılı hale getirilir. Kişisel veri işleniyorsa veri işleme sözleşmesi yapılır.',
          en: 'Which data is sent to the model is decided in the project analysis. Personal data is minimised or masked where possible, and the OpenAI API data-use terms and access logging are written down at proposal stage. Where personal data is processed, a data processing agreement is put in place.',
        },
      },
      {
        question: { tr: 'Hangi sistemlere bağlanabilir?', en: 'Which systems can it connect to?' },
        answer: {
          tr: 'API’si veya veri dışa aktarımı olan çoğu sisteme: web sitesi ve uygulamalar, CRM, ERP, destek araçları, doküman depoları ve iç veritabanları. Hangi bağlantının mümkün olduğu proje analizinde sistem bazında doğrulanır.',
          en: 'Most systems with an API or data export: websites and apps, CRM, ERP, helpdesk tools, document stores and internal databases. Which connections are feasible is verified system by system during the project analysis.',
        },
      },
      {
        question: { tr: 'ChatGPT yerine başka model kullanılabilir mi?', en: 'Can we use a model other than ChatGPT?' },
        answer: {
          tr: 'Evet. Entegrasyonu model sağlayıcıdan bağımsız bir katmanla kurarız; Claude veya açık kaynak modeller de kullanılabilir. Seçim kalite, maliyet ve veri gereksinimlerinize göre yapılır.',
          en: 'Yes. We build the integration behind a provider-independent layer, so Claude or open-source models can also be used. The choice follows your quality, cost and data requirements.',
        },
      },
    ],
    landing: {
      quickAnswer: {
        tr: 'ChatGPT entegrasyonu, OpenAI API’yi web sitenize, uygulamanıza, CRM veya destek sisteminize bağlayarak cevaplama, özetleme ve sınıflandırma gibi işleri otomatikleştirmektir. Figtures kullanım senaryosunu ve veri akışını KVKK’ya göre netleştirir, iş listesini ve eforu çıkarır, projeye özel ekip kurar. Örnek bant {price:general.mvp}; KDV hariçtir, kesin fiyat proje analizinden sonra belirlenir.',
        en: 'ChatGPT integration development connects the OpenAI API to your website, app, CRM or helpdesk so it can answer, summarise and classify inside your workflows. Figtures maps the use case and data flow, writes the task list and effort estimate, and assembles a team for the project. Example band: {price:general.mvp}, excl. VAT; final price after project analysis.',
      },
      costBands: [{ profile: 'general', scope: 'mvp' }],
      costBandNote: {
        tr: 'Maliyet tahmincisinde ChatGPT entegrasyonu için ayrı bir profil yoktur; gösterilen bant en yakın profil olan “Genel / özel yazılım” MVP bandıdır. Tek bir akışa sınırlı entegrasyonlar bu bandın altında kalabilir. OpenAI API kullanım ücreti ayrıca oluşur.',
        en: 'The cost estimator has no separate profile for ChatGPT integrations; the band shown is the MVP band of the closest profile, “General / custom software”. Integrations limited to a single flow can fall below it. OpenAI API usage is billed separately.',
      },
      caseStudies: ['p3'],
      caseStudyNote: {
        tr: 'Outfica’da Figtures ürün ve arayüz tasarımını yaptı; yapay zeka modelleri Outfica’ya aittir. Case study sayfası Figtures’ın rolünü ayrıca açıklar.',
        en: 'On Outfica, Figtures did the product and interface design; the AI models belong to Outfica. The case study page sets out Figtures’ role separately.',
      },
      sections: [
        {
          title: { tr: 'ChatGPT entegrasyonu nerelerde kullanılır?', en: 'Where ChatGPT integrations are used' },
          items: [
            { tr: 'Müşteri destek: gelen talepleri sınıflandırma ve yanıt taslağı', en: 'Customer support: classifying incoming requests and drafting replies' },
            { tr: 'Web sitesi ve uygulama içinde doküman tabanlı soru-cevap (RAG)', en: 'Document-grounded Q&A (RAG) inside your website or app' },
            { tr: 'CRM ve satış: görüşme özetleri, teklif ve e-posta taslakları', en: 'CRM and sales: call summaries, proposal and email drafts' },
            { tr: 'Operasyon: belge okuma, veri çıkarma ve raporlama', en: 'Operations: reading documents, extracting data and reporting' },
          ],
        },
        {
          title: { tr: 'Güvenlik ve kalite', en: 'Safety and quality' },
          items: [
            { tr: 'Kaynak gösteren cevaplar ve emin olunmadığında insana devretme', en: 'Answers that cite sources, with hand-off to a person when uncertain' },
            { tr: 'Örnek sorularla kalite testleri ve maliyet izleme', en: 'Quality tests on sample questions and cost monitoring' },
            { tr: 'Model sağlayıcıdan bağımsız mimari', en: 'An architecture that is not locked to one model provider' },
          ],
        },
      ],
      guideSlugs: ['ai-development-company-turkey'],
    },
  },
  {
    slug: 'claude-automation-solutions',
    title: { tr: 'Claude Tabanlı Otomasyon Çözümleri', en: 'Claude-Powered Automation Solutions' },
    seoTitle: {
      tr: 'Claude Otomasyon Product Studio | Güvenli İş Akışı Otomasyonu',
      en: 'Claude Automation Product Studio | Safe Workflow Automation',
    },
    seoDescription: {
      tr: 'Doküman işleme, operasyon otomasyonu ve karar destek akışları — human-in-the-loop Claude entegrasyonu.',
      en: 'Document processing, ops automation, and decision-support flows with human-in-the-loop Claude integration.',
    },
    heroTitle: {
      tr: 'Claude ile güvenli ve kontrollü otomasyon akışları geliştiriyoruz.',
      en: 'We build safe, controlled automation workflows with Claude.',
    },
    intro: {
      tr: 'Doküman odaklı ve süreç odaklı işlerde Claude kullanarak ekiplerin karar ve uygulama hızını artırıyoruz.',
      en: 'For documentation-heavy and process-heavy workflows, we use Claude to increase team decision and execution speed.',
    },
    legitimacyNote: {
      tr: 'Not: Figtures, Anthropic veya Claude ile resmi partnerlik/temsil iddiasında bulunmaz.',
      en: 'Note: Figtures does not claim official partnership or endorsement by Anthropic or Claude.',
    },
    whoFor: [
      { tr: 'İç bilgi akışını hızlandırmak isteyen ürün ve operasyon ekipleri', en: 'Product and operations teams accelerating internal knowledge flows' },
      { tr: 'Doküman/rapor süreçlerini yarı-otonom hale getirmek isteyen şirketler', en: 'Companies semi-automating document/report workflows' },
    ],
    whatWeBuild: [
      { tr: 'İç bilgi asistanları ve karar destek akışları', en: 'Internal knowledge assistants and decision-support flows' },
      { tr: 'Doküman özetleme ve sınıflandırma pipeline’ları', en: 'Document summarization and classification pipelines' },
      { tr: 'Human-in-the-loop onay mekanizmaları', en: 'Human-in-the-loop approval mechanisms' },
    ],
    useCases: [
      { tr: 'Operasyon SOP asistanları', en: 'Operational SOP assistants' },
      { tr: 'İç dokümanlardan aksiyon çıkarımı', en: 'Action extraction from internal documentation' },
      { tr: 'Riskli akışlarda onaylı otomasyon', en: 'Approved automation for risk-sensitive flows' },
    ],
    outcomes: [
      { tr: 'Ekip içi karar döngülerinde hızlanma', en: 'Faster internal decision cycles' },
      { tr: 'Bilgiye erişimde daha düşük sürtünme', en: 'Lower friction in access to knowledge' },
    ],
    delivery: [
      { tr: 'Use-case analizi -> Prompt/guardrail -> Entegrasyon -> Monitoring', en: 'Use-case analysis -> Prompt/guardrail -> Integration -> Monitoring' },
    ],
    relatedServiceSlugs: ['ai-automation-agents', 'ai-development', 'custom-software-development'],
    relatedProjectSlugs: ['lcw'],
    relatedBlogSlugs: ['evaluation-debt-in-ai-product-features'],
    relatedToolIds: ['customer-reply-generator', 'project-cost-estimator'],
    faqs: [
      {
        question: { tr: 'Claude hangi iş akışlarında uygundur?', en: 'Which workflows are suitable for Claude?' },
        answer: { tr: 'Özellikle doküman ağırlıklı, karar desteği gereken ve insan onayı ile ilerleyen süreçlerde uygundur.', en: 'It is especially useful in documentation-heavy, decision-support, and human-approved workflows.' },
      },
      {
        question: { tr: 'Sadece danışmanlık mı, geliştirme de var mı?', en: 'Do you only consult, or also implement?' },
        answer: { tr: 'Mimari planlama yanında doğrudan ürün entegrasyonu ve operasyonel teslimat da yapıyoruz.', en: 'We provide architecture planning and direct product integration/execution.' },
      },
    ],
  },
  {
    slug: 'cursor-codex-engineering-workflows',
    title: { tr: 'Cursor/Codex Mühendislik Workflow Kurulumu', en: 'Cursor/Codex Engineering Workflow Setup' },
    seoTitle: {
      tr: 'Cursor & Codex Product Studio | AI Destekli Yazılım Geliştirme',
      en: 'Cursor & Codex Product Studio | AI-Assisted Software Development',
    },
    seoDescription: {
      tr: 'Cursor, Codex ve agent workflow kurulumu: review guardrails, repo context ve ekip adoption playbook.',
      en: 'Cursor, Codex, and agent workflow setup: review guardrails, repo context, and team adoption playbooks.',
    },
    heroTitle: {
      tr: 'AI coding araçlarıyla mühendislik hızını artırırken kaliteyi koruyoruz.',
      en: 'We increase engineering velocity with AI coding tools while protecting quality.',
    },
    intro: {
      tr: 'Cursor/Codex kullanımını ekip standartları, review disiplini ve release güvenliği ile birleştiriyoruz.',
      en: 'We combine Cursor/Codex usage with team standards, review discipline, and release safety.',
    },
    legitimacyNote: {
      tr: 'Not: Figtures bağımsız danışmanlık ve uygulama hizmeti sunar; Cursor veya Codex’in resmi temsilcisi değildir.',
      en: 'Note: Figtures provides independent consulting and implementation and is not an official representative of Cursor or Codex.',
    },
    whoFor: [
      { tr: 'AI coding araçlarını ekipçe standartlaştırmak isteyen yazılım ekipleri', en: 'Software teams standardizing AI coding tool usage' },
      { tr: 'Teslimat hızını artırırken kaliteyi korumak isteyen ürün ekipleri', en: 'Product teams increasing speed without sacrificing quality' },
    ],
    whatWeBuild: [
      { tr: 'AI-assisted development workflow playbook’ları', en: 'AI-assisted development workflow playbooks' },
      { tr: 'Prompt/review/PR quality guardrail süreçleri', en: 'Prompt/review/PR quality guardrail processes' },
      { tr: 'Kod tabanı güvenlik ve doğrulama adımları', en: 'Codebase safety and validation steps' },
    ],
    useCases: [
      { tr: 'Feature delivery hızlandırma', en: 'Feature delivery acceleration' },
      { tr: 'Refactor ve migration sprintlerinde AI desteği', en: 'AI support in refactor and migration sprints' },
      { tr: 'Yeni ekip onboarding hızlandırma', en: 'Faster onboarding for new engineers' },
    ],
    outcomes: [
      { tr: 'Daha kısa lead time', en: 'Shorter lead time' },
      { tr: 'Daha tutarlı kod review kalitesi', en: 'More consistent code review quality' },
      { tr: 'AI kullanımında ekip standardizasyonu', en: 'Team-level standardization for AI usage' },
    ],
    delivery: [
      { tr: 'Workflow audit -> Team policy -> Pilot setup -> Scale-out', en: 'Workflow audit -> Team policy -> Pilot setup -> Scale-out' },
    ],
    relatedServiceSlugs: ['custom-software-development', 'startup-product-engineering', 'ai-development'],
    relatedProjectSlugs: ['lcw', 'ailem-aselsan'],
    relatedBlogSlugs: ['developer-experience-as-architecture', 'release-geometry-progressive-risk-slicing'],
    relatedToolIds: ['project-cost-estimator', 'customer-reply-generator'],
    faqs: [
      {
        question: { tr: 'Cursor/Codex kullanımı kaliteyi düşürür mü?', en: 'Can Cursor/Codex reduce code quality?' },
        answer: { tr: 'Doğru guardrail, review ve test disiplini ile kalite korunur ve teslimat hızı artar.', en: 'With proper guardrails, review, and testing discipline, quality is preserved while speed increases.' },
      },
      {
        question: { tr: 'Bu süreç mevcut ekibimize entegre olur mu?', en: 'Can this fit into our existing team process?' },
        answer: { tr: 'Evet, mevcut CI/CD ve review akışınıza uyumlu şekilde kademeli entegrasyon yapıyoruz.', en: 'Yes, we integrate incrementally into your existing CI/CD and review workflow.' },
      },
    ],
  },
  {
    slug: 'ai-powered-website-development',
    title: { tr: 'AI-Powered Website Development', en: 'AI-Powered Website Development' },
    seoTitle: {
      tr: 'AI Web Geliştirme Product Studio | SEO, Performans, Dönüşüm',
      en: 'AI Web Development Product Studio | SEO, Performance, Conversion',
    },
    seoDescription: {
      tr: 'AI destekli web siteleri: Core Web Vitals, schema, prerender ve growth-ready içerik mimarisi.',
      en: 'AI-powered websites with Core Web Vitals, structured data, prerender, and growth-ready content architecture.',
    },
    heroTitle: {
      tr: 'Web sitelerini statik vitrin değil, AI destekli dönüşüm yüzeyine çeviriyoruz.',
      en: 'We turn websites from static showcases into AI-enhanced conversion surfaces.',
    },
    intro: {
      tr: 'İçerik, arama, lead yakalama ve operasyon akışlarını AI ile birleştirerek daha güçlü bir web deneyimi kuruyoruz.',
      en: 'We combine content, search, lead capture, and operations workflows with AI for stronger web experiences.',
    },
    legitimacyNote: {
      tr: 'Not: Kullanılan platform/araç isimleri yalnızca teknik entegrasyon kapsamını ifade eder, resmi ortaklık iddiası değildir.',
      en: 'Note: Platform/tool names indicate technical integration scope only and do not imply official partnership.',
    },
    whoFor: [
      { tr: 'Lead-gen odaklı web yüzeylerini güçlendirmek isteyen ekipler', en: 'Teams strengthening lead-generation web surfaces' },
      { tr: 'İçerik üretimi ve kullanıcı etkileşimini AI ile geliştirmek isteyen şirketler', en: 'Companies improving content and engagement with AI' },
    ],
    whatWeBuild: [
      { tr: 'AI destekli içerik/yardımcı modüller', en: 'AI-enhanced content and assistant modules' },
      { tr: 'Akıllı site içi arama ve yönlendirme akışları', en: 'Intelligent on-site search and routing flows' },
      { tr: 'Lead qualification ve contact automation katmanı', en: 'Lead qualification and contact automation layer' },
    ],
    useCases: [
      { tr: 'B2B lead-gen website modernizasyonu', en: 'B2B lead-gen website modernization' },
      { tr: 'AI destekli satış öncesi bilgi asistanı', en: 'AI pre-sales knowledge assistant' },
    ],
    outcomes: [
      { tr: 'Daha yüksek qualified lead kalitesi', en: 'Higher quality of qualified leads' },
      { tr: 'Daha iyi kullanıcı keşif deneyimi', en: 'Better user discovery experience' },
    ],
    delivery: [
      { tr: 'IA/SEO discovery -> UX -> Engineering -> Iteration', en: 'IA/SEO discovery -> UX -> Engineering -> Iteration' },
    ],
    relatedServiceSlugs: ['web-development', 'ai-development', 'ui-ux-design'],
    relatedProjectSlugs: ['ztech', 'lcw'],
    relatedBlogSlugs: ['api-contract-evolution-without-breakage', 'consistency-budgets-streaming-interfaces'],
    relatedToolIds: ['youtube-seo-generator', 'instagram-caption-generator'],
    faqs: [
      {
        question: { tr: 'AI-powered website geliştirme neleri kapsar?', en: 'What does AI-powered website development include?' },
        answer: { tr: 'Akıllı içerik deneyimi, site içi arama, kullanıcı yönlendirme ve lead akışlarında AI entegrasyonlarını kapsar.', en: 'It includes AI integrations across content experiences, on-site search, user guidance, and lead workflows.' },
      },
      {
        question: { tr: 'Mevcut web sitemizi dönüştürebilir misiniz?', en: 'Can you transform our existing website?' },
        answer: { tr: 'Evet. Mevcut altyapıyı analiz edip adım adım AI destekli web dönüşümü planlıyoruz.', en: 'Yes. We audit your current stack and execute an incremental AI-powered website transformation.' },
      },
    ],
  },
  {
    slug: 'ai-powered-app-development',
    title: { tr: 'AI-Powered App Development', en: 'AI-Powered App Development' },
    seoTitle: {
      tr: 'AI Mobil Uygulama Product Studio | iOS & Android MVP',
      en: 'AI Mobile App Product Studio | iOS & Android MVP',
    },
    seoDescription: {
      tr: 'AI özellikli mobil uygulamalar: onboarding, push, offline-first ve ürün analitiği ile MVP teslimi.',
      en: 'AI-enabled mobile apps: onboarding, push, offline-first patterns, and product analytics in MVP delivery.',
    },
    heroTitle: {
      tr: 'Mobil ürünlerinize gerçek kullanıcı değerine bağlı AI özellikleri ekliyoruz.',
      en: 'We add AI features to mobile products tied to real user value.',
    },
    intro: {
      tr: 'AI destekli mobil deneyimler için ürün stratejisi, UX ve mühendisliği tek hat üzerinde yürütüyoruz.',
      en: 'For AI-powered mobile experiences, we align product strategy, UX, and engineering in one lane.',
    },
    legitimacyNote: {
      tr: 'Not: Platform ve model tercihleri proje ihtiyacına göre belirlenir; resmi partnerlik iddiası içermez.',
      en: 'Note: Platform and model choices are made per project needs and do not imply official partnerships.',
    },
    whoFor: [
      { tr: 'Mobil uygulamasına AI özellikleri eklemek isteyen ekipler', en: 'Teams adding AI features into mobile apps' },
      { tr: 'Yeni AI-first mobil ürün çıkaran startup’lar', en: 'Startups launching AI-first mobile products' },
    ],
    whatWeBuild: [
      { tr: 'Mobil AI asistanları ve akıllı onboarding', en: 'Mobile AI assistants and smart onboarding' },
      { tr: 'İçerik özetleme ve kişiselleştirme özellikleri', en: 'Summarization and personalization features' },
      { tr: 'Mobil + backend AI orchestration entegrasyonu', en: 'Mobile + backend AI orchestration integration' },
    ],
    useCases: [
      { tr: 'AI destekli kullanıcı asistanı', en: 'AI-assisted user companion' },
      { tr: 'Mobilde otomatik içerik üretimi/öneri', en: 'Automated in-app content generation/recommendation' },
    ],
    outcomes: [
      { tr: 'Daha iyi aktivasyon ve kullanıcı etkileşimi', en: 'Better activation and user engagement' },
      { tr: 'Mobil deneyimde daha yüksek kişiselleştirme', en: 'Higher personalization in mobile experiences' },
    ],
    delivery: [
      { tr: 'Use-case discovery -> UX prototyping -> Mobile + API build -> Validation', en: 'Use-case discovery -> UX prototyping -> Mobile + API build -> Validation' },
    ],
    relatedServiceSlugs: ['mobile-app-development', 'ai-development', 'startup-product-engineering'],
    relatedProjectSlugs: ['ailem-aselsan', 'kolektif'],
    relatedBlogSlugs: ['offline-first-mobile-sync-conflict-discipline'],
    relatedToolIds: ['brand-kit-generator', 'project-cost-estimator'],
    faqs: [
      {
        question: { tr: 'AI özellikleri mobil performansı düşürür mü?', en: 'Do AI features hurt mobile performance?' },
        answer: { tr: 'Doğru mimari ile AI çağrılarını optimize edip kullanıcı deneyimini koruyoruz.', en: 'With the right architecture, we optimize AI calls and preserve user experience performance.' },
      },
      {
        question: { tr: 'Hem iOS hem Android için AI özellikleri sunabilir misiniz?', en: 'Can you deliver AI features for both iOS and Android?' },
        answer: { tr: 'Evet. Ürün hedefinize göre tek planla iki platformda tutarlı deneyim kuruyoruz.', en: 'Yes. We deliver a consistent experience across both platforms under one product plan.' },
      },
    ],
  },
  {
    slug: 'custom-ai-tools-for-business',
    title: { tr: 'Custom AI Tools for Business Teams', en: 'Custom AI Tools for Business Teams' },
    seoTitle: {
      tr: 'Özel AI Araç Product Studio | Ürün, Pazarlama, Operasyon',
      en: 'Custom AI Tools Product Studio | Product, Marketing, Ops',
    },
    seoDescription: {
      tr: 'İç ekip ve müşteri yüzü için özel AI araçları: prompt guardrails, API entegrasyonu ve güvenilir çıktı.',
      en: 'Custom AI tools for internal teams and customers: prompt guardrails, API integration, and trustworthy output.',
    },
    heroTitle: {
      tr: 'İş ekipleriniz için özel AI araçlarını uçtan uca geliştiriyoruz.',
      en: 'We build custom AI tools for your business teams end-to-end.',
    },
    intro: {
      tr: 'Pazarlama, içerik, operasyon ve ürün ekiplerinin tekrar eden işlerini hızlandıran özel araçlar tasarlıyoruz.',
      en: 'We design custom tools that speed up repetitive workflows across marketing, content, operations, and product teams.',
    },
    legitimacyNote: {
      tr: 'Not: Kullanılan üçüncü taraf araçlar, ürün ihtiyaçlarına göre teknik entegrasyon bileşeni olarak değerlendirilir.',
      en: 'Note: Third-party tools are selected as technical integration components based on product requirements.',
    },
    whoFor: [
      { tr: 'Genel amaçlı AI araçları yerine kendi işine özel çözüm isteyen ekipler', en: 'Teams needing business-specific tools beyond generic AI products' },
      { tr: 'İç operasyon sürecini ürünleştirmek isteyen şirketler', en: 'Companies productizing internal operations workflows' },
    ],
    whatWeBuild: [
      { tr: 'İç ekip paneli + AI işlem katmanı', en: 'Internal team panel + AI processing layer' },
      { tr: 'İçerik/brief/rapor üretim araçları', en: 'Content/brief/report generation tools' },
      { tr: 'Kural tabanlı + model tabanlı hibrit otomasyon', en: 'Hybrid rule-based + model-based automation' },
      { tr: 'Google Analytics, Facebook/Instagram (Meta) ve CRM verisini birleştiren operasyon panelleri', en: 'Operations panels combining Google Analytics, Facebook/Instagram (Meta), and CRM data' },
    ],
    useCases: [
      { tr: 'Pazarlama ekipleri için campaign copilot', en: 'Campaign copilot for marketing teams' },
      { tr: 'Operasyon ekipleri için süreç asistanı', en: 'Process assistant for operations teams' },
      { tr: 'Ürün ekipleri için AI destekli iç araçlar', en: 'AI-powered internal tools for product teams' },
    ],
    outcomes: [
      { tr: 'Ekip başına daha yüksek operasyon kapasitesi', en: 'Higher operational capacity per team' },
      { tr: 'Daha standart ve tekrar edilebilir süreçler', en: 'More standardized and repeatable workflows' },
    ],
    delivery: [
      { tr: 'Workflow discovery -> Tool spec -> Build -> Team rollout', en: 'Workflow discovery -> Tool spec -> Build -> Team rollout' },
    ],
    relatedServiceSlugs: ['ai-automation-agents', 'custom-software-development', 'ai-development'],
    relatedProjectSlugs: ['lcw', 'ailem-aselsan'],
    relatedBlogSlugs: ['developer-experience-as-architecture', 'release-geometry-progressive-risk-slicing'],
    relatedToolIds: ['roi-calculator', 'project-cost-estimator', 'job-posting-writer'],
    faqs: [
      {
        question: { tr: 'Hazır AI aracı yerine neden custom tool?', en: 'Why choose a custom tool over an off-the-shelf AI tool?' },
        answer: { tr: 'Custom tool, mevcut süreçlerinize ve veri akışınıza tam uyum sağlar; ekip verimliliğini daha sürdürülebilir artırır.', en: 'A custom tool fits your workflows and data model precisely, enabling more sustainable team productivity gains.' },
      },
      {
        question: { tr: 'Bu araçları mevcut sistemlerimize bağlayabilir misiniz?', en: 'Can you integrate these tools with our existing systems?' },
        answer: { tr: 'Evet. CRM, CMS, proje yönetimi veya iç API’lerle entegre çalışacak şekilde tasarlıyoruz.', en: 'Yes. We design integrations with CRM, CMS, project management systems, and internal APIs.' },
      },
    ],
  },
];

const solutions: SolutionDefinition[] = solutionDefinitions.map((solution) => ({
  ...solution,
  faqs: resolveLandingFaqs(solution.faqs),
}));

const solutionMap = new Map(solutions.map((solution) => [solution.slug, solution]));

export function getAllSolutions() {
  return solutions;
}

export function getSolutionBySlug(slug: string) {
  return solutionMap.get(slug) || null;
}

export function getSolutionsBySlugs(slugs: string[]) {
  return slugs
    .map((slug) => getSolutionBySlug(slug))
    .filter((solution): solution is SolutionDefinition => Boolean(solution));
}

export function getFeaturedSolutionSlugs() {
  return [
    'chatgpt-integration-development',
    'claude-automation-solutions',
    'cursor-codex-engineering-workflows',
    'ai-powered-website-development',
  ];
}

export function getRelatedSolutionSlugsForService(serviceSlug: string) {
  return solutions
    .filter((solution) => solution.relatedServiceSlugs.includes(serviceSlug))
    .map((solution) => solution.slug)
    .slice(0, 4);
}

export function getRelatedSolutionSlugsForProject(projectSlug: string) {
  return solutions
    .filter((solution) => solution.relatedProjectSlugs.includes(projectSlug))
    .map((solution) => solution.slug)
    .slice(0, 4);
}

export function getRelatedSolutionSlugsForTool(toolId: string) {
  return solutions
    .filter((solution) => solution.relatedToolIds.includes(toolId))
    .map((solution) => solution.slug)
    .slice(0, 4);
}

export function getRelatedSolutionSlugsForBlog(blogSlug: string) {
  return solutions
    .filter((solution) => solution.relatedBlogSlugs.includes(blogSlug))
    .map((solution) => solution.slug)
    .slice(0, 4);
}
